import { differenceInMinutes } from 'date-fns';
import DefectList from './DefectList';
import { COMMIT_TIMEOUT_MINUTES } from '../../constants/repositories';
import { COMMIT_STATUS } from '../../constants/api';
import EmptyCommitSimple from './EmptyEntities/EmptyCommitSimple';
export default class CommitSimple extends EmptyCommitSimple {
    constructor(commit, defects, codeCoverage) {
        super();
        this.languages = null;
        this.options = null;
        this.fixFilesCount = 0;
        this.id = this.parseProperty(commit, 'id');
        this.analysisCount = this.parseProperty(commit, 'analysisCount');
        this.datetime = this.parseProperty(commit, 'datetime');
        this.updatedAt = this.parseProperty(commit, 'updatedAt');
        this.finishedAnalysisCount = this.parseProperty(commit, 'finishedAnalysisCount');
        this.hash = this.parseProperty(commit, 'hash');
        this.status = this.parseProperty(commit, 'status');
        this.errorCount = this.parseProperty(commit, 'errorCount');
        this.errorDiff = this.parseProperty(commit, 'errorDiff');
        this.message = this.parseProperty(commit, 'message');
        this.warningCount = this.parseProperty(commit, 'warningCount');
        this.warningDiff = this.parseProperty(commit, 'warningDiff');
        this.codeCoverage = codeCoverage;
        if ('languages' in commit) {
            this.languages = this.parseProperty(commit, 'languages');
        }
        if ('options' in commit) {
            this.options = this.parseProperty(commit, 'options');
        }
        if ('fixFilesCount' in commit) {
            this.fixFilesCount = this.parseProperty(commit, 'fixFilesCount');
        }
        const issues = defects && 'commonIssues' in defects && !!defects.commonIssues ? this.parseProperty(defects, 'commonIssues') : [];
        const totalNodes = defects && 'totalNodes' in defects ? this.parseProperty(defects, 'totalNodes') : 0;
        this.defectList = new DefectList(issues, totalNodes);
    }
    getPercentage() {
        throw new Error('Implement me!');
    }
    isTimedOut(datetime) {
        const commitAfterLimit = differenceInMinutes(new Date(), new Date(Number(datetime ?? this.updatedAt))) > COMMIT_TIMEOUT_MINUTES && !this.isDone();
        return this.status === COMMIT_STATUS.TIMEOUT || commitAfterLimit;
    }
    isProcessing() {
        return this.status === COMMIT_STATUS.PROCESSING;
    }
    hasAnySuccessfulFinishedReports() {
        throw new Error('Implement me!');
    }
    isDone() {
        return this.status === COMMIT_STATUS.DONE;
    }
    isFailed() {
        return this.status === COMMIT_STATUS.FAIL;
    }
    getReports() {
        throw new Error('Implement me!');
    }
    getWarnings() {
        throw new Error('Implement me!');
    }
    getErrors() {
        throw new Error('Implement me!');
    }
    getIssuesCount() {
        return this.errorCount + this.warningCount;
    }
    getDiffCount() {
        return this.errorDiff + this.warningDiff;
    }
    calculatePercentage() {
        const allAnalysisFinished = this.analysisCount === null && this.finishedAnalysisCount && this.finishedAnalysisCount > 0;
        if (this.analysisCount === 0 || allAnalysisFinished) {
            return 100;
        }
        if (!this.analysisCount || !this.finishedAnalysisCount) {
            return 0;
        }
        const percentage = parseInt(((this.finishedAnalysisCount * 100) / this.analysisCount).toFixed(0));
        return Math.min(percentage, 100);
    }
    isMeetingThresholds() {
        if (!this.options?.thresholds) {
            return true;
        }
        const totalIssuesThresholds = this.options?.thresholds?.total ?? 0;
        const errorsThresholds = this.options?.thresholds?.error ?? 0;
        const warningThresholds = this.options?.thresholds?.warning ?? 0;
        const coveredBranchesThresholds = this.options?.thresholds?.coveredBranches ?? 0;
        const coveredFunctionsThresholds = this.options?.thresholds?.coveredFunctions ?? 0;
        const coveredLinesThresholds = this.options?.thresholds?.coveredLines ?? 0;
        const crossedTotalIssuesThresholds = !!totalIssuesThresholds && this.getIssuesCount() > totalIssuesThresholds;
        if (crossedTotalIssuesThresholds) {
            return false;
        }
        const crossedErrorsThreshold = !!errorsThresholds && this.errorCount > errorsThresholds;
        if (crossedErrorsThreshold) {
            return false;
        }
        const crossedWarningThreshold = !!warningThresholds && this.warningCount > warningThresholds;
        if (crossedWarningThreshold) {
            return false;
        }
        const crossedCoveredBranchesThreshold = !!coveredBranchesThresholds && !!this.codeCoverage?.digest?.getBranches() && (this.codeCoverage.digest.getBranches() ?? 0) < coveredBranchesThresholds;
        if (crossedCoveredBranchesThreshold) {
            return false;
        }
        const crossedCoveredFunctionsThreshold = !!coveredFunctionsThresholds && !!this.codeCoverage?.digest?.getFunctions() && (this.codeCoverage.digest.getFunctions() ?? 0) < coveredFunctionsThresholds;
        if (crossedCoveredFunctionsThreshold) {
            return false;
        }
        const crossedCoveredLinesThreshold = !!coveredLinesThresholds && !!this.codeCoverage?.digest?.getLines() && (this.codeCoverage.digest.getLines() ?? 0) < coveredLinesThresholds;
        if (crossedCoveredLinesThreshold) {
            return false;
        }
        return true;
    }
    isAnalysisStillRunning() {
        if (this.isTimedOut()) {
            return false;
        }
        if (this.isProcessing()) {
            return true;
        }
        if (this.status < COMMIT_STATUS.DONE) {
            return true;
        }
        return false;
    }
    getAnalysisPercentage() {
        return !this.isTimedOut() ? this.calculatePercentage() : 100;
    }
    getShortHash() {
        return this.hash.substring(0, 8);
    }
}
